import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { ReadingDocView } from "@psy/shared";
import { ASSISTANT_NAME } from "./brand";
import { IconBack, IconClose, IconDownload } from "./icons";
import { openLink } from "./platform";

export type DocSection = ReadingDocView["sections"][number];

export function Spinner() {
  return <div className="spinner" role="status" aria-label="Загрузка" />;
}

/** Заглушка-«скелет» на время загрузки: блоки с мягким мерцанием вместо голого спиннера. */
export function Skeleton({ rows = 3, tall = false }: { rows?: number; tall?: boolean }) {
  return (
    <div className="skeletons" role="status" aria-label="Загрузка">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={`skeleton`} />
      ))}
    </div>
  );
}

/** Ошибка загрузки с кнопкой «Повторить». */
export function LoadError({ text = "Не удалось загрузить.", onRetry }: { text?: string; onRetry: () => void }) {
  return (
    <div className="empty">
      <p>{text}</p>
      <button className="outline" style={{ marginTop: 12 }} onClick={onRetry}>
        Повторить
      </button>
    </div>
  );
}

/** true, пока у устройства есть сеть (события online/offline браузера). */
export function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

/** Полноэкранный слой. dark — экраны результатов и карт, light — «Прогнозы». */
export function Overlay({
  title,
  onClose,
  tone = "dark",
  back = false,
  action,
  children,
}: {
  title?: string;
  onClose: () => void;
  tone?: "dark" | "light";
  back?: boolean;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`overlay ${tone === "light" ? "light" : "dark"}`} role="dialog" aria-label={title}>
      <div className="bar">
        <button className="iconbtn" onClick={onClose} aria-label={back ? "Назад" : "Закрыть"}>
          {back ? <IconBack /> : <IconClose />}
        </button>
        <h2>{title}</h2>
        <div className="bar-actions">{action}</div>
      </div>
      <div className="body">{children}</div>
    </div>
  );
}

export function useToast(): [ReactNode, (text: string) => void] {
  const [text, setText] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback((t: string) => {
    setText(t);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setText(null), 3200);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return [text ? <div className="toast" role="status">{text}</div> : null, show];
}

/** «14091987» → «14.09.1987». */
export function maskDate(raw: string): string {
  const d = raw.replace(/\D/gu, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}.${d.slice(2)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 4)}.${d.slice(4)}`;
}

export function isFullDate(v: string): boolean {
  return /^\d{2}\.\d{2}\.\d{4}$/u.test(v);
}

const MONTH_NAMES = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];
const DAYS_IN_MONTH = (month: number, year: number) => new Date(year, month, 0).getDate();

/** Короткий щелчок через Web Audio API — без файла, срабатывает на каждую смену значения в колесе. */
let tickCtx: AudioContext | null = null;
function playTick() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    tickCtx ??= new Ctx();
    if (tickCtx.state === "suspended") void tickCtx.resume();
    const osc = tickCtx.createOscillator();
    const gain = tickCtx.createGain();
    osc.type = "square";
    osc.frequency.value = 1100;
    gain.gain.setValueAtTime(0.045, tickCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, tickCtx.currentTime + 0.035);
    osc.connect(gain).connect(tickCtx.destination);
    osc.start();
    osc.stop(tickCtx.currentTime + 0.04);
  } catch {
    // тихо игнорируем — звук не критичен
  }
}

const ITEM_H = 44;

function WheelColumn({
  values,
  labels,
  index,
  onChange,
}: {
  values: number[];
  labels?: string[];
  index: number;
  onChange: (i: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const lastIndex = useRef(index);
  const scrollTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ top: index * ITEM_H, behavior: "auto" });
    lastIndex.current = index;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    window.clearTimeout(scrollTimer.current);
    const i = Math.round(el.scrollTop / ITEM_H);
    if (i !== lastIndex.current && i >= 0 && i < values.length) {
      lastIndex.current = i;
      playTick();
    }
    scrollTimer.current = window.setTimeout(() => {
      const settled = Math.round(el.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(values.length - 1, settled));
      el.scrollTo({ top: clamped * ITEM_H, behavior: "smooth" });
      onChange(clamped);
    }, 120);
  }, [values.length, onChange]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const want = index * ITEM_H;
    if (Math.abs(el.scrollTop - want) > ITEM_H / 2) el.scrollTo({ top: want, behavior: "smooth" });
  }, [index]);

  return (
    <div className="wheel-col" ref={ref} onScroll={onScroll}>
      <div className="wheel-pad" style={{ height: ITEM_H * 2 }} />
      {values.map((v, i) => (
        <div key={v} className={`wheel-item${i === index ? " on" : ""}`} style={{ height: ITEM_H }}>
          {labels ? labels[i] : v}
        </div>
      ))}
      <div className="wheel-pad" style={{ height: ITEM_H * 2 }} />
    </div>
  );
}

/** Колесо даты рождения в три колонки (день / месяц / год), со звуком и плавным доскроллом до ближайшего деления. */
export function DateWheel({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const now = new Date();
  const minYear = now.getFullYear() - 100;
  const maxYear = now.getFullYear() - 6;
  const parsed = isFullDate(value) ? value.split(".").map(Number) : null;
  const [day, setDay] = useState(parsed?.[0] ?? 15);
  const [month, setMonth] = useState(parsed?.[1] ?? 6);
  const [year, setYear] = useState(parsed?.[2] ?? Math.round((minYear + maxYear) / 2));

  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  const daysInMonth = DAYS_IN_MONTH(month, year);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const emit = (d: number, m: number, y: number) => {
    const safeDay = Math.min(d, DAYS_IN_MONTH(m, y));
    onChange(`${String(safeDay).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`);
  };

  return (
    <div className="wheel-date">
      <WheelColumn
        values={days}
        index={Math.min(day, days.length) - 1}
        onChange={(i) => {
          const d = days[i]!;
          setDay(d);
          emit(d, month, year);
        }}
      />
      <WheelColumn
        values={Array.from({ length: 12 }, (_, i) => i + 1)}
        labels={MONTH_NAMES}
        index={month - 1}
        onChange={(i) => {
          const m = i + 1;
          setMonth(m);
          emit(day, m, year);
        }}
      />
      <WheelColumn
        values={years}
        index={years.indexOf(year)}
        onChange={(i) => {
          const y = years[i]!;
          setYear(y);
          emit(day, month, y);
        }}
      />
    </div>
  );
}

export function DateField({
  label,
  value,
  onChange,
  autoFocus,
  readOnly,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  autoFocus?: boolean;
  readOnly?: boolean;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        className="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="ДД.ММ.ГГГГ"
        value={value}
        autoFocus={autoFocus}
        readOnly={readOnly}
        onChange={(e) => onChange?.(maskDate(e.target.value))}
      />
    </label>
  );
}

export const TIER_TITLE = { free: "Бесплатный", basic: "Базовый", full: "Полный" } as const;

export function formatUntil(iso: string | null, timeZone: string): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("ru-RU", { timeZone, day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleDateString("ru-RU");
  }
}

/**
 * Текст разбора: строки «• пункт» показываются маркированным списком, «1. шаг» — нумерованным, остальное — абзацами.
 * Модули отдают списки простыми строками, поэтому формат ответа сервера не меняется.
 */
export function RichText({ text, className }: { text: string; className?: string }) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length > 0 && lines.every((l) => l.startsWith("• "))) {
    return (
      <ul className={`bullets${className ? ` ${className}` : ""}`}>
        {lines.map((l, i) => (
          <li key={i}>{l.slice(2)}</li>
        ))}
      </ul>
    );
  }
  if (lines.length > 0 && lines.every((l) => /^\d+\.\s/u.test(l))) {
    return (
      <ol className={`steps${className ? ` ${className}` : ""}`}>
        {lines.map((l, i) => (
          <li key={i}>{l.replace(/^\d+\.\s/u, "")}</li>
        ))}
      </ol>
    );
  }
  return <p className={`text-block${className ? ` ${className}` : ""}`}>{text}</p>;
}

/** Первый пункт списка «• …» (или текст целиком) — для коротких превью. */
export function firstBullet(text: string): string {
  const first = text.split("\n").map((l) => l.trim()).find(Boolean) ?? "";
  return first.startsWith("• ") ? first.slice(2) : first;
}

/** Раздел разбора: заголовок и подпункты (плюсы, зона внимания, рекомендации) или обычный текст. */
export function SectionBlock({ section, heading }: { section: DocSection; heading?: string | null }) {
  const title = heading === undefined ? section.heading : (heading ?? undefined);
  return (
    <div className="item">
      {title && <h3>{title}</h3>}
      {section.parts && section.parts.length > 0 ? (
        section.parts.map((p, i) => (
          <div key={`${p.label}-${i}`}>
            <h4>{p.label}</h4>
            <RichText text={p.text} />
          </div>
        ))
      ) : (
        <p className="text-block">{section.body}</p>
      )}
    </div>
  );
}

export function LockedList({ doc, onPaywall }: { doc: ReadingDocView; onPaywall: () => void }) {
  if (doc.locked.length === 0) return null;
  return (
    <>
      {doc.locked.map((l) => (
        <div className="locked" key={l.id}>
          <span>🔒</span>
          <span>
            {l.heading} · <small>{l.requires === "deep" ? "Полный" : "Базовый"}</small>
          </span>
        </div>
      ))}
      <button className="primary" style={{ marginTop: 10 }} onClick={onPaywall}>
        Открыть полностью
      </button>
    </>
  );
}

/**
 * «Получить разбор полностью»: ИИ переписывает тот же материал (который клиент уже видит короткими блоками)
 * в один живой связный текст. Грузится только по нажатию и один раз кэшируется на сервере.
 */
export function ExpandButton({ load, label = "Получить разбор полностью" }: { load: () => Promise<{ title: string; text: string }>; label?: string }) {
  const [state, setState] = useState<"idle" | "loading" | "disabled" | "error" | { title: string; text: string }>("idle");

  if (state === "idle") {
    return (
      <button
        className="outline"
        style={{ marginTop: 10 }}
        onClick={() => {
          setState("loading");
          load().then(
            (r) => setState(r),
            (e) => setState(e?.code === "ai_disabled" ? "disabled" : "error"),
          );
        }}
      >
        {label}
      </button>
    );
  }
  if (state === "loading") {
    return (
      <div className="item" style={{ marginTop: 10 }}>
        <Spinner />
      </div>
    );
  }
  if (state === "disabled") {
    return (
      <div className="item" style={{ marginTop: 10 }}>
        <p className="warn">Эта функция пока отключена.</p>
      </div>
    );
  }
  if (state === "error") {
    return (
      <div className="item" style={{ marginTop: 10 }}>
        <p className="warn">Не получилось собрать полный разбор. Попробуйте чуть позже.</p>
        <button className="outline" onClick={() => setState("idle")}>Повторить</button>
      </div>
    );
  }
  return (
    <div className="item expanded-reading" style={{ marginTop: 10 }}>
      {state.text.split("\n\n").map((p, i) => (
        <RichText key={i} text={p} />
      ))}
    </div>
  );
}

/**
 * «Скачать PDF»: красиво оформленный отчёт (без ИИ, тот же контент) — только на Полном тарифе.
 * Открывает файл через blob-ссылку (Telegram WebView не всегда умеет сохранять напрямую).
 */
export function PdfButton({ load, compact }: { load: () => Promise<{ url: string }>; compact?: boolean }) {
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");

  const click = () => {
    setState("loading");
    load().then(
      ({ url }) => {
        openLink(url);
        setState("idle");
      },
      () => setState("error"),
    );
  };

  if (compact) {
    return (
      <button className="pdfbtn" disabled={state === "loading"} onClick={click} aria-label="Скачать PDF">
        <IconDownload />
        <span>{state === "loading" ? "Готовим…" : state === "error" ? "Не вышло, ещё раз" : "Скачать PDF"}</span>
      </button>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <button className="outline" disabled={state === "loading"} onClick={click}>
        {state === "loading" ? "Готовим PDF…" : "Скачать PDF"}
      </button>
      {state === "error" && <p className="warn">Не удалось собрать PDF. Попробуйте ещё раз.</p>}
    </div>
  );
}

export function Notes({ doc }: { doc: ReadingDocView }) {
  return (
    <div className="notes">
      {doc.cautions.map((c) => (
        <p className="warn" key={c}>
          ⚠️ {c}
        </p>
      ))}
      {doc.disclaimers.map((d) => (
        <p key={d}>
          <i>{d}</i>
        </p>
      ))}
    </div>
  );
}

export interface AiSection { id: string; heading: string; body: string }

/** Подробный разбор: запускается и опрашивает статус, пока разбор не готов. */
export function AiReportButton({ load, pdfLoad }: { load: () => Promise<AiPollResult>; pdfLoad?: () => Promise<{ url: string }> }) {
  const [state, setState] = useState<"idle" | "loading" | "disabled" | "error" | { title: string; sections: AiSection[] }>("idle");
  const [partial, setPartial] = useState<AiSection[]>([]);

  const poll = (attempt: number) => {
    load().then(
      (r) => {
        if (r.status === "done") setState({ title: r.title, sections: r.sections });
        else if (r.status === "running" && attempt < 80) {
          setPartial(r.sections);
          setTimeout(() => poll(attempt + 1), 3000);
        }
        else setState("error");
      },
      (e) => setState(e?.code === "ai_disabled" ? "disabled" : "error"),
    );
  };

  if (state === "idle") {
    return (
      <button className="outline" style={{ marginTop: 10 }} onClick={() => {
        setState("loading");
        poll(0);
      }}>
        Подробный разбор с {ASSISTANT_NAME}
      </button>
    );
  }
  if (state === "loading") {
    return (
      <div className="item" style={{ marginTop: 10 }}>
        {partial.map((s) => (
          <div key={s.id} style={{ marginTop: 12 }}>
            <h4>{s.heading}</h4>
            <RichText text={s.body} />
          </div>
        ))}
        <Spinner />
        <p className="muted">Дописываем разбор…</p>
      </div>
    );
  }
  if (state === "disabled") {
    return (
      <div className="item" style={{ marginTop: 10 }}>
        <p className="warn">Эта функция пока отключена.</p>
      </div>
    );
  }
  if (state === "error") {
    return (
      <div className="item" style={{ marginTop: 10 }}>
        <p className="warn">Не получилось собрать разбор. Попробуйте чуть позже.</p>
        <button className="outline" onClick={() => setState("idle")}>Повторить</button>
      </div>
    );
  }
  return (
    <div className="item" style={{ marginTop: 10 }}>
      {state.sections.map((s) => (
        <div key={s.id} style={{ marginTop: 12 }}>
          <h4>{s.heading}</h4>
          <RichText text={s.body} />
        </div>
      ))}
      {pdfLoad && <PdfButton load={pdfLoad} />}
    </div>
  );
}

export type AiPollResult =
  | { title: string; status: "running"; sections: AiSection[] }
  | { title: string; status: "error" }
  | { title: string; status: "done"; sections: AiSection[] };
